/**
 * CurrencySelector — Sélecteur de devise pour propositions et contre-offres.
 *
 * Règles UX (spec currency-v3) :
 * - Devises communes : affichées en premier avec badge "Compatible"
 * - Aucune devise commune : afficher deux listes séparées (their / mine)
 * - proposedCurrency fourni (contre-offre) : checkbox "Accepter la devise proposée"
 * - Aperçu de conversion en temps réel quand la devise sélectionnée ≠ devise source
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCommonCurrencies, ccySym, ccyName, ccyStripeOk, NOT_CARD_PAYABLE_NOTE, rateDateLabel } from '@/utils/currencyUtils';
import CurrencyService from '@/services/CurrencyService';
import './CurrencySelector.css';

/**
 * Props :
 *  myCurrencies      string[]  devises de l'émetteur de la proposition
 *  theirCurrencies   string[]  devises du destinataire
 *  theirFirstName    string    prénom du destinataire (pour les libellés)
 *  theirPrimary      string    devise principale du destinataire (badge ⭐)
 *  proposedCurrency  string    devise de la proposition reçue (contre-offre uniquement)
 *  amountCcy         string    devise dans laquelle est exprimé `amount` (source de conversion)
 *                              Defaults à myCurrencies[0] si absent — mais passer explicitement
 *                              évite les erreurs quand la primary n'est pas la première du tableau.
 *  amount            number    montant courant (aperçu de conversion)
 *  value             string    devise sélectionnée (controlled)
 *  onChange          fn(str)   callback de sélection
 *  disabled          bool
 */
export default function CurrencySelector({
  myCurrencies = ['EUR'],
  theirCurrencies = ['EUR'],
  theirFirstName = '',
  theirPrimary = null,
  proposedCurrency = null,
  amountCcy = null,
  amount = 0,
  value = null,
  onChange = () => {},
  onConvert = null,
  disabled = false,
}) {
  const [acceptProposed, setAcceptProposed] = useState(false);
  const [conversion, setConversion] = useState(null);
  const [converting, setConverting] = useState(false);
  const [rateTs, setRateTs] = useState(null);

  const common = useMemo(
    () => getCommonCurrencies(myCurrencies, theirCurrencies),
    [myCurrencies, theirCurrencies],
  );
  const hasCommon = common.length > 0;

  // Initialisation au montage : proposedCurrency > première commune dans l'ordre de l'annonce visée
  // (sa devise principale quand elle est commune) > première de l'annonce visée > EUR
  useEffect(() => {
    if (value) return;
    if (proposedCurrency) {
      setAcceptProposed(true);
      onChange(proposedCurrency);
    } else {
      // première commune dans l'ordre de l'annonce visée, sinon sa première devise — payable par carte
      const ordre = [...theirCurrencies.filter(c => common.includes(c)), ...theirCurrencies, ...myCurrencies];
      onChange(ordre.find(ccyStripeOk) || 'EUR');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Conversion temps réel quand la devise sélectionnée change
  useEffect(() => {
    const sourceCcy = amountCcy || proposedCurrency || myCurrencies[0] || 'EUR';
    if (!amount || !value || value === sourceCcy) {
      setConversion(null);
      onConvert?.(null); // eslint-disable-line react-hooks/exhaustive-deps
      return;
    }

    let cancelled = false;
    setConverting(true);
    CurrencyService.convert(amount, sourceCcy, value)
      .then(res => {
        if (!cancelled) {
          setConversion(res);
          setConverting(false);
          setRateTs(Date.now());
          onConvert?.(res.convertedAmount); // eslint-disable-line react-hooks/exhaustive-deps
        }
      })
      .catch(() => {
        if (!cancelled) { setConverting(false); onConvert?.(null); } // eslint-disable-line react-hooks/exhaustive-deps
      });
    return () => { cancelled = true; };
  }, [amount, value, proposedCurrency, amountCcy, myCurrencies]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAcceptProposed = useCallback((checked) => {
    setAcceptProposed(checked);
    if (checked && proposedCurrency) {
      onChange(proposedCurrency);
      setConversion(null);
    }
  }, [proposedCurrency, onChange]);

  const handleSelect = useCallback((ccy) => {
    if (disabled || acceptProposed || !ccyStripeOk(ccy)) return;
    onChange(ccy);
  }, [disabled, acceptProposed, onChange]);

  // Toutes les devises à afficher (communes en premier, puis les autres)
  const allCurrencies = useMemo(() => {
    const commonSet = new Set(common);
    const others = [...new Set([...myCurrencies, ...theirCurrencies])].filter(c => !commonSet.has(c));
    return [...common, ...others];
  }, [common, myCurrencies, theirCurrencies]);

  const sourceCcy = amountCcy || proposedCurrency || myCurrencies[0] || 'EUR';

  return (
    <div className={`cs ${disabled ? 'cs--disabled' : ''}`}>

      {/* Checkbox "Accepter la devise proposée" — affiché uniquement en contre-offre */}
      {proposedCurrency && (
        <label className="cs-accept">
          <input
            type="checkbox"
            checked={acceptProposed}
            onChange={e => handleAcceptProposed(e.target.checked)}
            disabled={disabled}
          />
          <span>
            Accepter directement la devise proposée&nbsp;:&nbsp;
            <strong>{ccySym(proposedCurrency)} {proposedCurrency}</strong>
          </span>
        </label>
      )}

      {/* Liste des devises — masquée si "Accepter la devise proposée" est coché */}
      {(!proposedCurrency || !acceptProposed) && (
        hasCommon ? (
          /* ── Cas 1 : devises communes — segmented control (variante D) ────── */
          <>
            <div className="cs-seg">
              {allCurrencies.map(ccy => {
                const isCommonCcy = common.includes(ccy);
                const isSelected = value === ccy;
                const isRecommended = !!(theirPrimary && ccy === theirPrimary && isCommonCcy);
                const isMineOnly = !isCommonCcy && myCurrencies.includes(ccy);
                const tier = isRecommended ? 'star' : isCommonCcy ? 'compat' : isMineOnly ? 'mine' : 'accepted';
                const icon = tier === 'star' ? '★' : tier === 'compat' ? '✓' : tier === 'mine' ? '◌' : '·';
                return (
                  <button
                    key={ccy}
                    type="button"
                    className={['cs-seg-item', `cs-seg-item--${tier}`, isSelected ? 'cs-seg-item--sel' : ''].filter(Boolean).join(' ')}
                    onClick={() => handleSelect(ccy)}
                    disabled={disabled || !ccyStripeOk(ccy)}
                    title={ccyStripeOk(ccy) ? ccyName(ccy) : `${ccyName(ccy)} — ${NOT_CARD_PAYABLE_NOTE}`}
                    data-unpayable={ccyStripeOk(ccy) ? undefined : 'true'}
                  >
                    <span className="cs-seg-code">{ccy}</span>
                    <span className="cs-seg-icon">{icon}</span>
                  </button>
                );
              })}
            </div>

            {/* Panneau d'info dynamique — apparaît après sélection */}
            {value && (() => {
              const isCommonCcy = common.includes(value);
              const isRecommended = !!(theirPrimary && value === theirPrimary && isCommonCcy);
              const isMineOnly = !isCommonCcy && myCurrencies.includes(value);
              const tier = isRecommended ? 'star' : isCommonCcy ? 'compat' : isMineOnly ? 'mine' : 'accepted';
              const them = theirFirstName || "l'annonceur";
              const msgs = {
                star:     { l1: `Devise de référence de ${them}.`,                              l2: `Aucun frais de change ne s'appliquera à cette transaction.` },
                compat:   { l1: `Devise déclarée par les deux parties.`,                        l2: `La transaction s'effectuera sans conversion ni frais supplémentaires.` },
                mine:     { l1: `Devise que vous avez déclarée, non reconnue par ${them}.`,     l2: `Une conversion pourra être requise de leur côté ; des frais peuvent s'appliquer.` },
                accepted: { l1: `Devise acceptée par ${them}, absente de votre profil.`,        l2: `Des frais de change seront probablement appliqués. Votre proposition reste valide.` },
              };
              const { l1, l2 } = msgs[tier];
              return (
                <div className={`cs-seg-info cs-seg-info--${tier}`}>
                  <strong>{value}</strong> : {l1}<br /><span>{l2}</span>
                </div>
              );
            })()}
          </>
        ) : (
          /* ── Cas 2 : aucune devise commune — deux listes séparées ─────────── */
          <div className="cs-no-compat">
            <div className="cs-no-compat-notice">
              Pas de devise en commun avec {theirFirstName || "l'annonceur"}, choisissez quand même celle qui vous convient.
            </div>
            <div className="cs-section">
              <div className="cs-section-label">
                Devises de {theirFirstName || "l'annonceur"}
              </div>
              <div className="cs-list">
                {theirCurrencies.map(ccy => (
                  <CurrencyItem
                    key={ccy}
                    ccy={ccy}
                    selected={value === ccy}
                    onSelect={handleSelect}
                    disabled={disabled}
                    recommended={theirPrimary && ccy === theirPrimary}
                  />
                ))}
              </div>
            </div>
            <div className="cs-section cs-section--mt">
              <div className="cs-section-label">Mes devises</div>
              <div className="cs-list">
                {myCurrencies.map(ccy => (
                  <CurrencyItem
                    key={ccy}
                    ccy={ccy}
                    selected={value === ccy}
                    onSelect={handleSelect}
                    disabled={disabled}
                    isMine={true}
                  />
                ))}
              </div>
            </div>
          </div>
        )
      )}

      {allCurrencies.filter(c => !ccyStripeOk(c)).map(c => (
        <div key={c} className="cs-not-payable" style={{ marginTop: 6, fontSize: 12, color: '#b45309' }} data-testid="cs-not-payable">
          {ccyName(c)} ({c}) : {NOT_CARD_PAYABLE_NOTE}. Choisissez une autre devise, le prix est converti au taux du jour.
        </div>
      ))}

      {/* Aperçu de conversion temps réel */}
      {value && amount > 0 && value !== sourceCcy && !acceptProposed && (
        <div className="cs-conv">
          <div className="cs-conv-row">
            <span className="cs-conv-from">
              {CurrencyService.formatAmount(amount, sourceCcy)}
            </span>
            <span className="cs-conv-arrow">→</span>
            {converting ? (
              <span className="cs-conv-loading">calcul…</span>
            ) : conversion ? (
              <span className="cs-conv-to">
                ≈ {CurrencyService.formatAmount(conversion.convertedAmount, value)}
              </span>
            ) : null}
          </div>
          {conversion && (
            <div className="cs-rate-info">
              1 {sourceCcy} ≈ {conversion.exchangeRate?.toFixed(2)} {value}
              {rateTs && (
                <span className="cs-rate-age">
                  {' '}· mis à jour il y a {Math.round((Date.now() - rateTs) / 60000) || '<1'} min
                </span>
              )}
              {rateDateLabel(conversion.exchangeRateDate, conversion.exchangeProvider) && (
                <span className="cs-rate-date" data-testid="cs-rate-date"> · {rateDateLabel(conversion.exchangeRateDate, conversion.exchangeProvider)}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CurrencyItem({ ccy, selected, onSelect, disabled, recommended = false, isMine = false }) {
  const pillType = recommended ? 'star' : isMine ? 'mine' : 'accepted';
  const pillIcon = recommended ? '★' : isMine ? '◌' : '';
  return (
    <button
      type="button"
      className={['cs-item', `cs-item--${pillType}`, selected ? 'cs-item--sel' : ''].filter(Boolean).join(' ')}
      onClick={() => onSelect(ccy)}
      disabled={disabled || !ccyStripeOk(ccy)}
      title={ccyStripeOk(ccy) ? ccyName(ccy) : `${ccyName(ccy)} — ${NOT_CARD_PAYABLE_NOTE}`}
      data-unpayable={ccyStripeOk(ccy) ? undefined : 'true'}
    >
      {pillIcon && <span className="cs-pill-icon">{pillIcon}</span>}
      <span className="cs-code">{ccy}</span>
    </button>
  );
}
